// Lightweight client diagnostics — no third-party SDK.
//
// Keeps a rolling buffer of the last N events (GraphQL errors, empty-list /
// data-loss signals, response summaries) in memory + localStorage so support
// can pull them from a complaining user's browser via window.__diag().
//
// If VUE_APP_CLIENT_LOG_URL is set, each event is also POSTed (fire-and-forget,
// keepalive) to that endpoint with the user's bearer token. When it is unset
// the module is purely local — so it can ship and be useful before the
// collection endpoint exists.

const MAX_EVENTS = 50
const LS_KEY = 'do_diag_buffer'
const REMOTE_URL = process.env.VUE_APP_CLIENT_LOG_URL

let buffer = []
try {
  buffer = JSON.parse(window.localStorage.getItem(LS_KEY)) || []
} catch (e) {
  buffer = []
}

function persist () {
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(buffer))
  } catch (e) {
    // localStorage full / unavailable — keep the in-memory buffer only.
  }
}

// Snapshot of the state that explains most silent failures (why a list is
// empty for one user but not another). Read lazily off the global app instance
// to avoid an import cycle with the store.
function snapshot () {
  const out = {
    time: new Date().toISOString(),
    graphqlEndpoint: process.env.VUE_APP_GRAPHQL_HTTP,
    env: process.env.VUE_APP_ENV
  }
  try {
    out.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (e) {}
  try {
    const store = window.doApp && window.doApp.$store
    if (store) {
      const s = store.state
      out.selectedDate = s.selectedDate
      // selectedRaceId lets us tell "no race selected yet" (null) apart from a
      // real selection when a race(id: null) error shows up. nullIdRaces counts
      // stored races missing an id — if >0, Deltafusor is returning null race
      // ids (which also breaks selection: selectRace guards `if (!id) return`).
      out.selectedRaceId = s.selectedRace && s.selectedRace.id
      out.racesInState = s.races ? Object.keys(s.races).length : null
      out.nullIdRaces = s.races ? Object.values(s.races).filter(r => r && r.id == null).length : null
      out.racesAfterFilters = (store.getters.getRaces || []).length
      out.navFilters = s.userConfig && s.userConfig.raceNavFilters
      out.username = s.account && s.account.user && s.account.user.username
      // hasUser distinguishes "Cognito session lapsed / not rehydrated"
      // (state.user null → token can't refresh → 403 Invalid token) from a
      // token bug where the session is fine. hasDeltafusorToken = a token is
      // present at all (it's stored under the legacy `coreApiToken` name).
      out.hasUser = !!(s.account && s.account.user)
      out.hasDeltafusorToken = !!(s.account && s.account.coreApiToken)
    }
  } catch (e) {}
  return out
}

export function logEvent (level, type, data) {
  let event
  try {
    event = { t: new Date().toISOString(), level, type, data }
    buffer.push(event)
    if (buffer.length > MAX_EVENTS) buffer = buffer.slice(-MAX_EVENTS)
    persist()
    // eslint-disable-next-line no-console
    const fn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log
    fn(`[diag:${type}]`, data)
    // Surface the latest error detail to the "something went wrong" banner so a
    // support screenshot is actionable.
    if (level === 'error') {
      try {
        const store = window.doApp && window.doApp.$store
        if (store) {
          const message = (data && (data.message || (data.errors && data.errors.join('; ')))) || ''
          store.commit('setLastError', { type, message, time: event.t })
        }
      } catch (e) {}
    }
    sendRemote(event)
  } catch (e) {
    // Diagnostics must never break the app.
  }
  return event
}

function sendRemote (event) {
  if (!REMOTE_URL) return
  try {
    const token = window.localStorage.getItem('coreapi_auth_token')
    if (!token) return
    // keepalive so the request survives a page unload; auth header means we
    // can't use sendBeacon (it can't set headers), so plain fetch it is.
    fetch(REMOTE_URL, {
      method: 'POST',
      keepalive: true,
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ event, ctx: snapshot() })
    }).catch(() => {})
  } catch (e) {
    // never throw from logging
  }
}

// A crashing render can re-fire many times a second; collapse a repeated error
// signature so we don't flood the buffer / remote endpoint with the same crash.
let lastErrKey = ''
let lastErrAt = 0
function throttled (key) {
  const now = Date.now()
  if (key === lastErrKey && now - lastErrAt < 2000) return true
  lastErrKey = key
  lastErrAt = now
  return false
}

function shortStack (err) {
  const stack = err && err.stack
  return stack ? String(stack).split('\n').slice(0, 6).join('\n') : undefined
}

// Capture uncaught render/runtime errors. The query-level logging (graphql-error,
// meetingsDated-loaded) only proves data arrived; if a component then throws
// while rendering it — the RunnerLastStarts class of crash — nothing else sees
// it. Wire this from main.js with the Vue constructor.
export function installGlobalErrorHandlers (Vue) {
  try {
    if (Vue && Vue.config) {
      const prev = Vue.config.errorHandler
      Vue.config.errorHandler = function (err, vm, info) {
        const component = vm && vm.$options && (vm.$options.name || vm.$options._componentTag)
        if (!throttled(`vue:${component}:${err && err.message}:${info}`)) {
          logEvent('error', 'render-error', {
            message: err && err.message,
            component,
            info,
            stack: shortStack(err)
          })
        }
        if (typeof prev === 'function') {
          prev.call(this, err, vm, info)
        } else {
          // eslint-disable-next-line no-console
          console.error(err)
        }
      }
    }
  } catch (e) {}
  try {
    window.addEventListener('error', function (e) {
      // Ignore resource-load errors (img/script) — they have no message/error.
      if (!e || (!e.message && !e.error)) return
      if (throttled(`win:${e.message}:${e.filename}:${e.lineno}`)) return
      logEvent('error', 'window-error', {
        message: e.message,
        source: e.filename,
        line: e.lineno,
        col: e.colno,
        stack: shortStack(e.error)
      })
    })
    window.addEventListener('unhandledrejection', function (e) {
      const reason = e && e.reason
      const message = reason && (reason.message || String(reason))
      if (throttled(`rej:${message}`)) return
      logEvent('error', 'unhandled-rejection', {
        message,
        stack: shortStack(reason)
      })
    })
  } catch (e) {}
}

export function getDiagnostics () {
  return { snapshot: snapshot(), events: buffer }
}

export function clearDiagnostics () {
  buffer = []
  persist()
}

if (typeof window !== 'undefined') {
  // Support/debug hook: `__diag()` in the console dumps the buffer + snapshot.
  window.__diag = () => {
    const d = getDiagnostics()
    // eslint-disable-next-line no-console
    console.log(JSON.stringify(d, null, 2))
    return d
  }
}
